// cSpell:ignore Andere Huurder privé Tuinonderhoud wassen
// Pure helpers behind how a settlement reads: the confirmed view rendered from
// its frozen snapshot, the wizard summary and the statement document. Kept out
// of the components so every screen formats the same way and the rules can be
// unit-tested.

import { OwnerDepartureDiff } from "./engine";
import { roundCents } from "./money.utils";
import {
  ApportionmentKey,
  CATEGORIES,
  CATEGORY_KEYS,
  CATEGORY_LABELS_NL,
  Category,
  ContractRef,
  DistributionMethod,
  IInvoiceFigures,
  Invoice,
  Settlement,
  SettlementSnapshot,
} from "./types";

// End date the contract mapping gives a contract without a stop date.
export const OPEN_ENDED_CONTRACT_END = "2999-12-31";

// Marker the engine appends to the breakdown key of a cost on one unit.
export const PRIVATE_KEY_SUFFIX = " (privé)";

const CATEGORY_SPEC_SEPARATOR = " — ";

// ── Frozen labels and dates ─────────────────────────────────────────────────

// A unit's label as it was when the settlement was confirmed. Undefined for
// snapshots frozen before the group's units were.
export const frozenUnitLabel = (
  snapshot: SettlementSnapshot | undefined,
  unitId: string,
): string | undefined =>
  snapshot?.groupUnits?.find(u => String(u.id) === String(unitId))?.label;

// The rental period a tenant was settled with: the dates frozen into their
// statement, so ending, editing or deleting the contract afterwards does not
// change a confirmed settlement. The live contract only for snapshots frozen
// before the statements were.
export const frozenContractDates = (params: {
  snapshot?: SettlementSnapshot;
  contractId: string;
  contracts: ContractRef[];
}): { from: string; to: string } | undefined => {
  const { snapshot, contractId, contracts } = params;
  const frozen = snapshot?.statements?.[`tenant:${contractId}`];
  if (frozen?.contractStart) {
    return {
      from: frozen.contractStart,
      to: frozen.contractEnd ?? OPEN_ENDED_CONTRACT_END,
    };
  }
  const live = contracts.find(c => c.id === contractId);
  return live ? { from: live.startDate, to: live.endDate } : undefined;
};

// The name of the apportionment key a category was split with, as it was on
// confirm: frozen in keyNames, and before that only on the statements (an
// older statement names its category in the broker's language). The live name
// only when neither has it; a key deleted since is still named as a key.
export const settledKeyName = (params: {
  snapshot?: SettlementSnapshot;
  keyId: string;
  category: Category;
  liveName?: string;
  deletedName: string;
}): string => {
  const { snapshot, keyId, category, liveName, deletedName } = params;
  const frozenName = snapshot?.keyNames?.[keyId];
  if (frozenName) return frozenName;

  const statementName = Object.values(snapshot?.statements ?? {})
    .flatMap(statement => statement.categoryGroups ?? [])
    .find(
      group =>
        !group.isPrivate &&
        !!group.apportionmentKeyName &&
        (group.categoryI18nKey
          ? group.categoryI18nKey === CATEGORY_KEYS[category]
          : group.categoryKey === CATEGORY_LABELS_NL[category]),
    )?.apportionmentKeyName;

  return statementName ?? liveName ?? deletedName;
};

// The key weights frozen on confirm, for the units the settlement counted
// (`unitIds`: the group's units, vacant ones included, as the engine divides
// by them; empty outside a group). A stale weight of a unit that left the
// group was not part of the split, so it must not inflate the frozen basis.
// An equal key stores no weights, so its denominator ("1 / n") would have to
// come from the units that exist later: freeze it as 1 per unit instead.
export const freezeKeyWeights = (params: {
  keys: ApportionmentKey[];
  unitIds: string[];
}): Record<string, Record<string, number>> => {
  const { keys, unitIds } = params;
  const weightsOf = (k: ApportionmentKey): Record<string, number> => {
    if (unitIds.length === 0) return { ...k.weights };
    if (k.isEqual) return Object.fromEntries(unitIds.map(id => [id, 1]));
    return Object.fromEntries(
      Object.entries(k.weights).filter(([id]) => unitIds.includes(id)),
    );
  };
  return Object.fromEntries(keys.map(k => [k.id, weightsOf(k)]));
};

// ── Costs ───────────────────────────────────────────────────────────────────

// Living on the group is what makes a cost a group cost. Costs created on the
// group before creation always stamped scope=common may still say "private";
// every screen of a group settlement reads them as common, the same way the
// engine settled them, so their breakdown keys match the frozen snapshot.
// groupCostRef children stay private: they are per-unit amounts on real units.
export const normalizeGroupSettlementCosts = (
  invoices: Invoice[],
  settlement: Pick<Settlement, "scope" | "groupId">,
): Invoice[] => {
  if (settlement.scope !== "group" || !settlement.groupId) return invoices;
  return invoices.map(i =>
    i.scope !== "common" &&
    !i.groupCostRef &&
    String(i.unitId) === String(settlement.groupId)
      ? { ...i, scope: "common" as const }
      : i,
  );
};

// The figures of each settled cost as the wizard read it, frozen into the
// snapshot (SettlementSnapshot.invoiceFigures).
export const buildInvoiceFigures = (
  invoices: Invoice[],
): Record<string, IInvoiceFigures> =>
  Object.fromEntries(
    invoices.map(invoice => [
      invoice.id,
      {
        propertyId: Number(invoice.unitId ?? invoice.groupId),
        amountExclVat: invoice.amountExclVat,
        vat: invoice.vatPct,
        periodFrom: invoice.periodFrom,
        periodTo: invoice.periodTo,
        target: invoice.target,
        ...(invoice.tenantSharePct != null
          ? { tenantSharePct: invoice.tenantSharePct }
          : {}),
        category: invoice.category,
      },
    ]),
  );

// A cost the way a confirmed settlement settled it: the figures it froze
// (invoiceFigures) over the live cost, so a cost edited since cannot change
// what the settlement shows. The single source of a settled cost's figures
// for every screen of a confirmed settlement. Snapshots from before the
// figures were frozen keep the live cost.
export const withFrozenFigures = <TInvoice extends Invoice>(
  invoice: TInvoice,
  snapshot?: Pick<SettlementSnapshot, "invoiceFigures">,
): TInvoice => {
  const frozen = snapshot?.invoiceFigures?.[invoice.id];
  if (!frozen) return invoice;
  return {
    ...invoice,
    amountExclVat: frozen.amountExclVat,
    vatPct: frozen.vat,
    periodFrom: frozen.periodFrom,
    periodTo: frozen.periodTo,
    target: frozen.target,
    tenantSharePct: frozen.tenantSharePct,
    category: frozen.category,
  };
};

// A departure settlement (see ISettledDeparture) settles its share of the
// parent group's costs next to the unit's own, and reads the group's costs as
// a group settlement does. Without a parent group, only the unit's own.
export const departureSettlementCosts = (
  invoices: Invoice[],
  departureGroupId?: string,
): Invoice[] =>
  departureGroupId
    ? normalizeGroupSettlementCosts(invoices, {
        scope: "group",
        groupId: departureGroupId,
      })
    : invoices;

// The broker's own specification of an "Other" cost ("Tuinonderhoud").
export const categorySpecOf = (inv: Invoice): string | undefined =>
  inv.category === "Other" ? inv.categoryOther?.trim() || undefined : undefined;

// "Andere — Tuinonderhoud, Ramen wassen": the category with what the broker
// specified for it. The specification is their own wording, never translated.
export const withCategorySpecs = (label: string, specs?: string[]): string =>
  specs && specs.length > 0
    ? `${label}${CATEGORY_SPEC_SEPARATOR}${specs.join(", ")}`
    : label;

const categoryOfKey = (key: string): string =>
  key.endsWith(PRIVATE_KEY_SUFFIX)
    ? key.slice(0, -PRIVATE_KEY_SUFFIX.length)
    : key;

// One order for the categories of a settlement on every screen and on the
// statement: the category list's own order, a category's group costs before
// its unit costs. Unknown keys go last. Object key order cannot be used: the
// settlement is stored in a JSON column that reorders its keys.
export const compareBreakdownKeys = (a: string, b: string): number => {
  const indexOf = (key: string) => {
    const index = CATEGORIES.indexOf(categoryOfKey(key) as Category);
    return index === -1 ? CATEGORIES.length : index;
  };
  const byCategory = indexOf(a) - indexOf(b);
  if (byCategory !== 0) return byCategory;
  const byScope =
    Number(a.endsWith(PRIVATE_KEY_SUFFIX)) -
    Number(b.endsWith(PRIVATE_KEY_SUFFIX));
  if (byScope !== 0) return byScope;
  return a.localeCompare(b);
};

export const sortBreakdownKeys = (keys: string[]): string[] =>
  [...keys].sort(compareBreakdownKeys);

// One row of the owner card: a category's fixed share, its vacancy, or the
// difference with one departure settlement (F85).
export interface IOwnerCardRow {
  key: string;
  kind: "base" | "vacancy" | "departure_difference";
  excl: number;
  btw: number;
  // A departure_difference row: the departure and its tenant.
  departure?: Pick<OwnerDepartureDiff, "settlementId" | "tenantName">;
}

// The rows of an owner's card, in the categories' order. The fixed share
// (`baseByCat`, departure differences included) and the vacancy share the
// category's VAT (`vatByCat`) minus the differences' own VAT: the base takes
// its rounded part, the vacancy the rest, so the rows add up to the owner's
// total and VAT exactly. Each departure difference is a row of its own with
// its own VAT, of either sign. Without vatByCat (older snapshots) the VAT
// follows the category's average rate (`fallbackRateOf`); without
// `departureDiffsByCat` (owners frozen before it) the difference stays in
// the fixed share, as it was shown then.
export const ownerCardRows = (params: {
  baseByCat: Record<string, number>;
  vacancyByCat: Record<string, number>;
  vatByCat?: Record<string, number>;
  departureDiffsByCat?: Record<string, OwnerDepartureDiff[]>;
  fallbackRateOf: (key: string) => number;
}): IOwnerCardRow[] => {
  const { baseByCat, vacancyByCat, vatByCat, fallbackRateOf } = params;
  const diffsByCat = params.departureDiffsByCat ?? {};
  const keys = sortBreakdownKeys(
    Array.from(
      new Set([
        ...Object.keys(baseByCat),
        ...Object.keys(vacancyByCat),
        ...Object.keys(diffsByCat),
      ]),
    ),
  );
  const rows: IOwnerCardRow[] = [];
  for (const key of keys) {
    const diffs = diffsByCat[key] ?? [];
    const diffExcl = diffs.reduce((sum, diff) => sum + diff.amount, 0);
    const diffVat = diffs.reduce((sum, diff) => sum + diff.vat, 0);
    const base = roundCents((baseByCat[key] ?? 0) - diffExcl);
    const vacancy = roundCents(vacancyByCat[key] ?? 0);
    const exactVat =
      vatByCat?.[key] != null ? roundCents(vatByCat[key] - diffVat) : undefined;
    const ownerExcl = base + vacancy;
    const rate =
      exactVat != null && Math.abs(ownerExcl) > 0.001
        ? exactVat / ownerExcl
        : fallbackRateOf(key);
    const hasBase = Math.abs(base) > 0.001;
    const baseBtw = hasBase ? roundCents(base * rate) : 0;
    if (hasBase) {
      rows.push({ key, kind: "base", excl: base, btw: baseBtw });
    }
    if (Math.abs(vacancy) > 0.001) {
      rows.push({
        key,
        kind: "vacancy",
        excl: vacancy,
        btw:
          exactVat != null
            ? roundCents(exactVat - baseBtw)
            : roundCents(vacancy * rate),
      });
    }
    for (const diff of diffs) {
      rows.push({
        key,
        kind: "departure_difference",
        excl: roundCents(diff.amount),
        btw: roundCents(diff.vat),
        departure: {
          settlementId: diff.settlementId,
          tenantName: diff.tenantName,
        },
      });
    }
  }
  return rows;
};

// The key that split the owners' part of a consumption or metering-company
// category over the group's units (the excess or remainder that falls on the
// owners, see allocate): the category's chosen key, or the equal split when
// none was chosen. A category split by key already names its key.
export const ownerPartKeyName = (params: {
  method?: DistributionMethod;
  keyId?: string;
  keyNameOf: (keyId: string) => string | undefined;
  equalKeyName?: string;
}): string | undefined => {
  const { method, keyId, keyNameOf, equalKeyName } = params;
  if (method !== "consumption" && method !== "company") return undefined;
  return keyId ? keyNameOf(keyId) : equalKeyName;
};

// ── Parties on the lease ────────────────────────────────────────────────────

interface IContractMemberLike {
  roles?: string[];
  account?: { id?: number } | null;
}

// Who is on a lease, by role rather than by the order the API lists them in:
// every tenant (co-tenants are all party to the settlement), the parents or
// guardians who are not tenants themselves, and the payer the settlement is
// addressed to — the first tenant, or a parent when the lease has no tenant.
export const contractParties = <TMember extends IContractMemberLike>(params: {
  members: TMember[];
  nameOf: (member: TMember) => string;
  tenantRole: string;
  parentRole: string;
}): {
  tenantNames: string[];
  parentNames: string[];
  payerAccountId?: number;
} => {
  const { members, nameOf, tenantRole, parentRole } = params;
  const hasRole = (member: TMember, role: string) =>
    (member.roles ?? []).includes(role);
  const tenants = members.filter(m => hasRole(m, tenantRole));
  const parents = members.filter(
    m => hasRole(m, parentRole) && !hasRole(m, tenantRole),
  );
  const payer = tenants[0] ?? parents[0];
  return {
    tenantNames: tenants.map(nameOf).filter(Boolean),
    parentNames: parents.map(nameOf).filter(Boolean),
    payerAccountId: payer?.account?.id ?? undefined,
  };
};

// "Hans Huurder & Henry Huurder".
export const joinPartyNames = (names: string[]): string => names.join(" & ");
